import React from "react";
import { motion } from "framer-motion";
import { Check, Building2, Truck, ShieldCheck, Radio, TrendingUp, Clock } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import { ServiceHero, SectionHeading } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";
import { LeadForm } from "../components/site/LeadForm";
import { MENTORSHIP_MODULES } from "../data/content";

const ICONS = [Building2, Truck, ShieldCheck, Radio, TrendingUp];

const ModuleCard = ({ m, i }) => {
  const Icon = ICONS[i];
  return (
    <Reveal delay={i * 0.08}>
      <motion.div whileHover={{ y: -6 }} className="nm-card rounded-2xl p-7 h-full" data-testid={`mentorship-module-${i}`}>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-11 h-11 rounded-xl nm-gold-bg flex items-center justify-center shrink-0">
            <Icon size={20} className="text-[#0B132B]" />
          </span>
          <h3 className="font-head text-lg font-bold text-white leading-tight">{m.title}</h3>
        </div>
        <ul className="space-y-2.5">
          {m.items.map((it) => (
            <li key={it} className="flex items-start gap-3 text-gray-300 text-sm">
              <Check size={16} className="text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} />
              {it}
            </li>
          ))}
        </ul>
      </motion.div>
    </Reveal>
  );
};

export default function EliteMentorship() {
  return (
    <div data-testid="mentorship-page">
      <Seo title="Elite Trucking Business Mentorship Program" description="A 6–8 week intensive trucking business mentorship: formation & authority setup, fleet operations, DOT compliance, dispatching, and financial growth strategy. $8,000 investment." />
      <ServiceHero eyebrow="Elite Trucking Business Mentorship Program" title="BUILD IT. STRUCTURE IT." highlight="LAUNCH IT. SCALE IT."
        subtitle="Starting a trucking company takes more than purchasing a truck and obtaining an MC number. It requires industry knowledge, operational systems, regulatory compliance, financial planning, and a strategy for long-term success."
        image={IMAGES.sunsetTruck} price="$8,000 · 6–8 Week Intensive" />

      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <Reveal>
            <p className="text-gray-300 leading-relaxed">
              At Next Mile Compliance LLC, our Elite Trucking Business Mentorship Program is designed for aspiring trucking entrepreneurs,
              new motor carriers, and existing fleet owners who are ready to establish, operate, and grow a professional trucking business.
            </p>
            <p className="text-gray-400 leading-relaxed mt-4">
              This intensive 6–8 week mentorship provides personalized guidance, hands-on business development, and real-world trucking industry training.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="nm-card rounded-2xl p-6 text-center min-w-[220px]" data-testid="mentorship-duration">
              <Clock className="mx-auto text-[#D4AF37] mb-3" size={26} />
              <p className="font-head text-3xl font-black nm-gold-text">6–8 WEEKS</p>
              <p className="text-gray-400 text-xs mt-1 tracking-wider">PROGRAM DURATION</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20 bg-[#0B132B] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Curriculum" center className="mb-12">WHAT'S INCLUDED IN YOUR <span className="nm-gold-text">MENTORSHIP?</span></SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MENTORSHIP_MODULES.map((m, i) => <ModuleCard key={m.title} m={m} i={i} />)}
          </div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="nm-card rounded-3xl overflow-hidden border-[#D4AF37]/40 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)]">
              <div className="nm-gold-bg p-8 text-center">
                <p className="font-mono text-xs tracking-[0.3em] text-[#0B132B]/80 mb-2">PROGRAM INVESTMENT</p>
                <p className="font-head text-6xl font-black text-[#0B132B]" data-testid="mentorship-price">$8,000</p>
                <p className="text-[#0B132B]/80 font-semibold text-sm mt-1">6–8 Week Intensive Mentorship</p>
              </div>
              <div className="p-8 sm:p-10">
                <h3 className="font-head text-2xl font-extrabold text-white mb-3">THE NEXT MILE <span className="nm-gold-text">EXPERIENCE</span></h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  This is more than a training course. It is a structured business mentorship designed to help you move from uncertainty to informed business ownership.
                  Participants receive personalized guidance, practical resources, business setup assistance, and direct mentorship throughout the program.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed mt-4">
                  Your investment provides access to our structured mentorship curriculum, business development guidance, operational training, and implementation support throughout the program.
                </p>
                <a href="#mentorship-apply"
                  className="btn-sheen mt-7 inline-flex items-center justify-center gap-2 nm-gold-bg text-[#0B132B] font-bold px-7 py-3.5 rounded-full hover:scale-[1.02] transition-transform"
                  data-testid="mentorship-apply-cta">
                  APPLY FOR ELITE MENTORSHIP TODAY
                </a>
                <p className="text-[11px] text-gray-500 leading-relaxed mt-6" data-testid="mentorship-disclaimer">
                  Results and business timelines vary based on individual circumstances, regulatory approvals, equipment availability, and participant commitment.
                  Revenue and profitability are not guaranteed.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="mentorship-apply" className="relative py-20 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading eyebrow="Ready to take your business to the next mile?" className="mb-5">STOP GUESSING. <span className="nm-gold-text">START BUILDING.</span></SectionHeading>
            <p className="text-gray-400 leading-relaxed">Start building with strategy, structure, and professional guidance.</p>
            <p className="font-head text-xl font-bold text-white mt-8">NEXT MILE COMPLIANCE LLC</p>
            <p className="nm-gold-text font-semibold">Where Compliance Meets Opportunity.</p>
          </div>
          <div>
            <LeadForm source="mentorship" defaultService="Elite Trucking Business Mentorship" submitLabel="APPLY FOR ELITE MENTORSHIP" />
          </div>
        </div>
      </section>

      <CTABand eyebrow="Where compliance meets opportunity" title="Apply for Elite Mentorship today." button="APPLY NOW" />
    </div>
  );
}
